/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import CommonPopupWithImageComponent from 'Components/common/commonPopupWithImageComponent';
import crmIntegrationState from 'Stores/crmIntegration';
import userDetail from 'Stores/userDetail';
import { Utils } from 'Utils/utils';
import integrationDataMap from './integrationDataMap';

const defaultPopupValues = {
  title: '',
  description: '',
  btnText: '',
};

const getCRMName = crmType =>
  integrationDataMap[crmType]?.title
    ?.split(' ')
    ?.map(data => `${data.substring(0, 1).toUpperCase()}${data.substring(1)}`)
    ?.join(' ') ?? crmType;

const getAdminNotConnectedMsg = props => {
  const { crmType, adminEmail, redirectToIntegrations } = props;
  const title = 'Connection Alert';
  const crmName = getCRMName(crmType);
  // redirectToIntegrations is made use to differentiate between integrations and contacts page
  const description =
    redirectToIntegrations !== undefined ? (
      <>
        Sorry {userDetail.userInfo.name}! We’re unable to connect to {crmName}{' '}
        CRM as the admin has not connected to it. Please write to your account
        administrator{' '}
        <a
          href={`mailto:${adminEmail}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {adminEmail}
        </a>{' '}
        or{' '}
        <a
          href="mailto:success@adapt.io"
          rel="noopener noreferrer"
          target="_blank"
        >
          success@adapt.io
        </a>{' '}
        to resolve.
      </>
    ) : (
      <>
        Please contact your account administrator{' '}
        <a
          href={`mailto:${adminEmail}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {adminEmail}
        </a>{' '}
        to connect and configure the {crmName} integration before proceeding.
        Please contact us at{' '}
        <a
          href="mailto:success@adapt.io"
          rel="noopener noreferrer"
          target="_blank"
        >
          success@adapt.io
        </a>{' '}
        to learn more.
      </>
    );

  const btnText = 'ok';
  // Go to integrations in contacts doesn't make sense since user wont be able to do anything until admin has connected
  // if (redirectToIntegrations) btnText = 'Go to Integrations';
  return { title, description, btnText };
};

const getUserNotConnectedMsg = props => {
  const { crmType, redirectToIntegrations } = props;
  const title = 'Connection Alert';
  const description = `Your ${getCRMName(
    crmType,
  )} CRM account is not connected! To proceed with the export, please connect your CRM with your Adapt account.`;

  let btnText = 'ok';
  if (redirectToIntegrations) btnText = 'Go to Integrations';
  return { title, description, btnText };
};

const authorizationRequiredMsg = props => {
  const { crmType, redirectToIntegrations } = props;
  const title = 'Connection Alert';
  const description = `Your ${getCRMName(
    crmType,
  )} CRM account is not connected or authorization expired! To proceed with the export, please connect your CRM or disconnect and connect if already connected with your Adapt account.`;

  let btnText = 'ok';
  if (redirectToIntegrations) btnText = 'Go to Integrations';
  return { title, description, btnText };
};

const getAdminConnectedToDifferentCrmMsg = props => {
  const { crmType, adminEmail, adminDomain, userDomain } = props;
  const title = 'Connection Alert';
  const description = (
    <>
      <div>Hey {userDetail.userInfo.name},</div>
      <div>
        It looks like the CRM account ({getCRMName(crmType)} - {userDomain})
        you’re trying to connect is different from the CRM account your admin is
        connected to ({getCRMName(crmType)} - {adminDomain}).
      </div>
      <div>
        Please write to your account administrator{' '}
        <a
          href={`mailto:${adminEmail}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {adminEmail}
        </a>{' '}
        or{' '}
        <a
          href="mailto:success@adapt.io"
          rel="noopener noreferrer"
          target="_blank"
        >
          success@adapt.io
        </a>{' '}
        to resolve.
      </div>
    </>
  );

  const btnText = 'ok';
  return { title, description, btnText };
};

const ConnectionErrorMsgWrapper = observer(props => {
  const { showConnectErrorMsg = {} } = crmIntegrationState;
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupValues, setPopupValues] = useState(defaultPopupValues);

  const closeClick = () => {
    crmIntegrationState.setShowConnectErrorMsg();
  };

  const actionBtnClick = () => {
    const { redirectToIntegrations = false } = showConnectErrorMsg;
    if (redirectToIntegrations)
      window.open(Utils.getCustomPagePath('INTEGRATIONS'), '_blank');
    crmIntegrationState.setShowConnectErrorMsg();
  };

  useEffect(() => {
    const { showPopup, responseCode } = showConnectErrorMsg;

    setShowErrorPopup(showPopup);
    if (showPopup)
      if (responseCode === 403)
        setPopupValues(getAdminConnectedToDifferentCrmMsg(showConnectErrorMsg));
      else if (responseCode === 412)
        setPopupValues(getAdminNotConnectedMsg(showConnectErrorMsg));
      else if (responseCode === 303)
        setPopupValues(getUserNotConnectedMsg(showConnectErrorMsg));
      else if (responseCode === 407)
        setPopupValues(authorizationRequiredMsg(showConnectErrorMsg));
  }, [showConnectErrorMsg]);

  const { title, description, btnText } = popupValues;

  return showErrorPopup ? (
    <CommonPopupWithImageComponent
      customParentClass="crm-connection-error-popup"
      titleText={title}
      description={description}
      btnText={btnText}
      actionBtnCallback={actionBtnClick}
      clickOutsidePopupCallback={closeClick}
      closeClickCallback={closeClick}
      showCloseBtn
    />
  ) : (
    ''
  );
});

export default ConnectionErrorMsgWrapper;
export { ConnectionErrorMsgWrapper };
